import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  LinearProgress,
  Collapse,
  InputAdornment,
  Switch,
  FormControlLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as ThemeIcon,
  Dashboard as DashboardIcon,
  ViewModule as LayoutIcon,
  Security as SecurityIcon,
  Notifications as NotificationIcon,
  Extension as IntegrationIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Build as FeatureIcon,
  Analytics as AnalyticsIcon,
  Code as CodeIcon,
  CloudDownload as ImportIcon,
  CloudUpload as ExportIcon,
  FileCopy as CloneIcon,
  Preview as PreviewIcon,
  Check as ValidateIcon,
  PlayArrow as ApplyIcon,
  PlayArrow,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Label as TagIcon,
  History as VersionIcon,
  Group as UsersIcon,
  LocationOn as LocationIcon,
  Assignment as RoleIcon,
  Public as GlobalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ColorLens as ColorIcon,
  FormatPaint as CustomizeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const TypeChip = styled(Chip)<{ profileType: string }>(({ theme, profileType }) => {
  const getColor = () => {
    switch (profileType) {
      case 'system': return { bg: '#f44336', color: '#fff' };
      case 'location': return { bg: '#2196f3', color: '#fff' };
      case 'user': return { bg: '#4caf50', color: '#fff' };
      case 'role': return { bg: '#ff9800', color: '#fff' };
      default: return { bg: '#e0e0e0', color: '#000' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 'bold',
  };
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customization-tabpanel-${index}`}
      aria-labelledby={`customization-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function CustomizationPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    isActive: true,
    isTemplate: false,
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalProfiles, setTotalProfiles] = useState(0);

  // Analytics
  const [analytics, setAnalytics] = useState({
    totalProfiles: 0,
    activeProfiles: 0,
    templatesCount: 0,
    typeBreakdown: [],
    featureUsage: [],
    adoptionRate: 0
  });

  // New profile form
  const [newProfile, setNewProfile] = useState({
    name: '',
    description: '',
    type: 'location',
    scope: {
      locations: [],
      users: [],
      roles: [],
      global: false
    },
    ui: {
      theme: {
        primary: '#1976d2',
        secondary: '#dc004e',
        darkMode: { enabled: false, auto: false }
      },
      layout: {
        sidebarCollapsed: false,
        compactMode: false,
        showWelcomeScreen: true,
        defaultDashboard: 'overview',
        menuStyle: 'sidebar'
      }
    },
    features: {
      reservations: { enabled: true, allowOnlineBooking: true },
      pricing: { dynamicPricing: false, seasonalRates: true },
      payments: { allowedMethods: ['credit_card', 'debit_card', 'cash'] }
    },
    isTemplate: false,
    tags: []
  });

  useEffect(() => {
    fetchProfiles();
    fetchTemplates();
    fetchAnalytics();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: filters.type,
        isActive: filters.isActive.toString(),
        isTemplate: filters.isTemplate.toString(),
        search: filters.search,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      const response = await api.get(`/customization/profiles?${params}`);
      setProfiles(response.data.profiles);
      setTotalProfiles(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/customization/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/customization/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleProfileClick = async (profile: any) => {
    try {
      const response = await api.get(`/customization/profiles/${profile._id}`);
      setSelectedProfile(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching profile details:', error);
    }
  };

  const handleCreateProfile = async () => {
    try {
      const response = await api.post('/customization/profiles', newProfile);
      setCreateDialogOpen(false);
      fetchProfiles();
      // Reset form
      setNewProfile({
        name: '',
        description: '',
        type: 'location',
        scope: { locations: [], users: [], roles: [], global: false },
        ui: {
          theme: { primary: '#1976d2', secondary: '#dc004e', darkMode: { enabled: false, auto: false } },
          layout: { sidebarCollapsed: false, compactMode: false, showWelcomeScreen: true, defaultDashboard: 'overview', menuStyle: 'sidebar' }
        },
        features: {
          reservations: { enabled: true, allowOnlineBooking: true },
          pricing: { dynamicPricing: false, seasonalRates: true },
          payments: { allowedMethods: ['credit_card', 'debit_card', 'cash'] }
        },
        isTemplate: false,
        tags: []
      });
    } catch (error) {
      console.error('Error creating profile:', error);
    }
  };

  const handleApplyProfile = async (profileId: string, targetType: string, targetId: string) => {
    try {
      const response = await api.post(`/customization/profiles/${profileId}/apply`, { targetType, targetId });
      alert('Profile applied successfully');
      fetchProfiles();
    } catch (error) {
      console.error('Error applying profile:', error);
    }
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <SettingsIcon />
              </Avatar>
              <Typography variant="h6">Total Profiles</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {analytics?.totalProfiles || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All customization profiles
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <PlayArrow />
              </Avatar>
              <Typography variant="h6">Active Profiles</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {analytics?.activeProfiles || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Currently in use
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                <StarIcon />
              </Avatar>
              <Typography variant="h6">Templates</Typography>
            </Box>
            <Typography variant="h4" color="info.main">
              {analytics?.templatesCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reusable templates
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <AnalyticsIcon />
              </Avatar>
              <Typography variant="h6">Adoption Rate</Typography>
            </Box>
            <Typography variant="h4" color="warning.main">
              {(analytics?.adoptionRate || 0).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active vs total profiles
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Profile Types Breakdown
          </Typography>
          <Stack spacing={2}>
            {(analytics?.typeBreakdown || []).map((type: any) => (
              <Box key={type._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TypeChip 
                    size="small" 
                    profileType={type._id}
                    label={type._id} 
                  />
                  <Typography sx={{ ml: 2 }}>
                    {type.count} total, {type.active} active
                  </Typography>
                </Box>
                <Typography variant="h6">
                  {type.count}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Popular Templates
          </Typography>
          <List>
            {(templates || []).slice(0, 5).map((template: any) => (
              <ListItem key={template._id}>
                <ListItemIcon>
                  <StarIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.description}
                />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setNewProfile(prev => ({ ...prev, ...template }));
                      setCreateDialogOpen(true);
                    }}
                  >
                    Use
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderProfilesTable = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search profiles..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="location">Location</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="role">Role</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.isTemplate}
                  onChange={(e) => handleFilterChange('isTemplate', e.target.checked)}
                />
              }
              label="Templates Only"
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Profile
              </Button>
              <Button
                variant="outlined"
                startIcon={<ImportIcon />}
              >
                Import
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchProfiles()}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((profile: any) => (
                <TableRow 
                  key={profile._id}
                  hover
                  onClick={() => handleProfileClick(profile)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {profile.isTemplate ? <StarIcon /> : <SettingsIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {profile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {profile.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TypeChip 
                      size="small" 
                      profileType={profile.type}
                      label={profile.type} 
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {profile.scope?.global && <Chip size="small" icon={<GlobalIcon />} label="Global" />}
                      {profile.scope?.users?.length > 0 && (
                        <Chip size="small" icon={<UsersIcon />} label={`${profile.scope?.users?.length} Users`} />
                      )}
                      {profile.scope?.locations?.length > 0 && (
                        <Chip size="small" icon={<LocationIcon />} label={`${profile.scope?.locations?.length} Locations`} />
                      )}
                      {profile.scope?.roles?.length > 0 && (
                        <Chip size="small" icon={<RoleIcon />} label={`${profile.scope?.roles?.length} Roles`} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      color={profile.isActive ? 'success' : 'default'}
                      label={profile.isActive ? 'Active' : 'Inactive'} 
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {profile?.createdBy?.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone">
                        <IconButton size="small">
                          <CloneIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export">
                        <IconButton size="small">
                          <ExportIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalProfiles}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  const renderTemplates = () => (
    <Grid container spacing={3}>
      {(templates || []).map((template: any) => (
        <Grid item xs={12} sm={6} md={4} key={template._id}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <StarIcon />
                </Avatar>
                <Typography variant="h6">{template.name}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {template.description}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TypeChip 
                  size="small" 
                  profileType={template.type}
                  label={template.type} 
                />
                {template.tags?.map((tag: string) => (
                  <Chip key={tag} size="small" label={tag} variant="outlined" />
                ))}
              </Stack>
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<PreviewIcon />}>
                Preview
              </Button>
              <Button size="small" startIcon={<AddIcon />}>
                Use Template
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Customization Framework
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure system behavior, UI themes, and feature settings with flexible profiles
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Profiles" />
          <Tab label="Templates" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderOverview()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderProfilesTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderTemplates()}
      </TabPanel>

      {/* Profile Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Customization Profile Details
        </DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Profile Information</Typography>
                <Typography><strong>Name:</strong> {selectedProfile?.name}</Typography>
                <Typography><strong>Type:</strong> {selectedProfile?.type}</Typography>
                <Typography><strong>Status:</strong> {selectedProfile?.isActive ? 'Active' : 'Inactive'}</Typography>
                <Typography><strong>Created:</strong> {selectedProfile?.createdAt ? new Date(selectedProfile.createdAt).toLocaleDateString() : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Scope</Typography>
                <Typography><strong>Global:</strong> {selectedProfile.scope?.global ? 'Yes' : 'No'}</Typography>
                <Typography><strong>Users:</strong> {selectedProfile.scope?.users?.length || 0}</Typography>
                <Typography><strong>Locations:</strong> {selectedProfile.scope?.locations?.length || 0}</Typography>
                <Typography><strong>Roles:</strong> {selectedProfile.scope?.roles?.length || 0}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />}>Edit</Button>
          <Button variant="outlined" startIcon={<CloneIcon />}>Clone</Button>
        </DialogActions>
      </Dialog>

      {/* Create Profile Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Customization Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Profile Name"
                value={newProfile.name}
                onChange={(e) => setNewProfile(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newProfile.type}
                  onChange={(e) => setNewProfile(prev => ({ ...prev, type: e.target.value }))}
                  label="Type"
                >
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="location">Location</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="role">Role</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newProfile.description}
                onChange={(e) => setNewProfile(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newProfile.isTemplate}
                    onChange={(e) => setNewProfile(prev => ({ ...prev, isTemplate: e.target.checked }))}
                  />
                }
                label="Create as Template"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateProfile}>Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}